import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent } from '../utils/track';
import './WeeklyPackSignup.css';

// Mailchimp audience "logicpuzzles" (the list the home page has always used).
// post-json + a JSONP callback lets us show the result inline instead of opening
// Mailchimp's hosted page in a new tab. Mailchimp sends its own confirmation
// email, so every subscriber has opted in twice.
const MC_BASE = 'https://logicpuzzles.us14.list-manage.com/subscribe/post-json';
const MC_U = 'a6e90c1ec9e7f4cea7d7c6994';
const MC_ID = '8d17fd0dc0';

function subscribe(email) {
  return new Promise((resolve, reject) => {
    const cb = `mc_cb_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
    const script = document.createElement('script');
    const cleanup = () => {
      delete window[cb];
      script.remove();
    };
    const timer = setTimeout(() => { cleanup(); reject(new Error('timeout')); }, 12000);
    window[cb] = (data) => {
      clearTimeout(timer);
      cleanup();
      resolve(data);
    };
    const params = new URLSearchParams({ u: MC_U, id: MC_ID, EMAIL: email, c: cb });
    params.set(`b_${MC_U}_${MC_ID}`, '');
    script.src = `${MC_BASE}?${params.toString()}`;
    script.onerror = () => { clearTimeout(timer); cleanup(); reject(new Error('network')); };
    document.body.appendChild(script);
  });
}

// Mailchimp error messages start with "0 - " and can contain HTML links.
const cleanMessage = (msg = '') => msg.replace(/^\d+\s*-\s*/, '').replace(/<[^>]+>/g, '');

function WeeklyPackSignup({ source, variant = 'card', title, text }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    try {
      const data = await subscribe(email.trim());
      const already = /already subscribed/i.test(data.msg || '');
      if (data.result === 'success' || already) {
        setStatus('success');
        setMessage(already
          ? "You're already on the list. Your next pack is on its way."
          : 'Almost done: check your inbox and confirm your email to get the first pack.');
        setEmail('');
        trackEvent('newsletter_signup', { event_category: 'engagement', event_label: source, list: 'weekly_pack' });
      } else {
        setStatus('error');
        setMessage(cleanMessage(data.msg) || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setMessage('We could not reach the sign-up service. Please try again in a moment.');
    }
  };

  return (
    <aside className={`wps wps--${variant} no-print`} aria-labelledby={`wps-title-${source}`}>
      <div className="wps__copy">
        <p className="wps__eyebrow">Free every Monday</p>
        <h2 id={`wps-title-${source}`} className="wps__title">{title || 'Get a new printable puzzle pack every week'}</h2>
        <p className="wps__text">
          {text || 'Six fresh logic puzzles plus a bonus logic grid, with answers, ready to print. One email a week, no spam, unsubscribe any time.'}
        </p>
      </div>
      {status === 'success' ? (
        <p className="wps__done" role="status">{message}</p>
      ) : (
        <form className="wps__form" onSubmit={onSubmit}>
          <label className="wps__label" htmlFor={`wps-email-${source}`}>Email address</label>
          <div className="wps__field">
            <input
              id={`wps-email-${source}`}
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <button type="submit" className="button spark" disabled={status === 'sending'}>
              {status === 'sending' ? 'Signing up...' : 'Send me the pack'}
            </button>
          </div>
          {status === 'error' && <p className="wps__error" role="alert">{message}</p>}
          <p className="wps__fine">
            By signing up you agree to get the weekly pack and occasional news about our puzzles and apps from
            Spark and Spiral Software Inc. See this week's pack any time at <Link to="/printable/weekly">logicpuzzles.ca/printable/weekly</Link>.
            {' '}<Link to="/privacy-policy">Privacy policy</Link>.
          </p>
        </form>
      )}
    </aside>
  );
}

export default WeeklyPackSignup;
